// Client-side printable receipts & statements (opens a print window).

const rm = (n) => "RM " + (Number(n) || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Escape any dynamic value before interpolating into the print HTML (prevents XSS).
const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const shell = (title, inner) => `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', ui-monospace, monospace; color: #111; margin: 0; padding: 32px; }
  .doc { max-width: 720px; margin: 0 auto; }
  .brand { font-size: 22px; font-weight: 800; letter-spacing: 1px; }
  .muted { color: #666; font-size: 12px; }
  h2 { font-size: 16px; border-bottom: 2px solid #111; padding-bottom: 6px; margin: 18px 0 12px; text-transform: uppercase; letter-spacing: 1px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  td, th { padding: 6px 4px; text-align: left; }
  th { border-bottom: 1px solid #999; text-transform: uppercase; font-size: 11px; color: #555; }
  .r { text-align: right; }
  .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; }
  .tot { border-top: 2px solid #111; font-weight: 800; font-size: 15px; margin-top: 8px; padding-top: 8px; }
  .foot { margin-top: 32px; font-size: 11px; color: #888; text-align: center; }
  .sign { margin-top: 48px; display: flex; justify-content: space-between; font-size: 12px; }
  .sign div { border-top: 1px solid #111; width: 200px; padding-top: 6px; text-align: center; }
</style></head><body><div class="doc">${inner}
<div class="foot">Generated ${esc(new Date().toLocaleString("en-MY"))} · This is a computer-generated document.</div>
</div></body></html>`;

// Render into a sandboxed off-screen iframe via srcdoc (no document.write, no inline scripts).
const openPrint = (html) => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) { console.warn("Print failed:", e); }
    setTimeout(() => iframe.remove(), 1000);
  };
  document.body.appendChild(iframe);
  iframe.srcdoc = html;
};

const customerLine = (l) => {
  const bits = [l.customerName];
  if (l.icPassport) bits.push("IC/Passport: " + l.icPassport);
  if (l.phone) bits.push("Tel: " + l.phone);
  return bits.filter(Boolean).join(" · ");
};

export function printReceipt(loan, p) {
  const code = loan.customerCode || loan.code;
  const inner = `
    <div class="brand">DIAMOND SHARP</div>
    <div class="muted">Branch ${esc(loan.branch || "")} · Payment Receipt</div>
    <h2>Receipt</h2>
    <div class="row"><span>Receipt No.</span><b>${esc((p.paymentId || "").slice(0, 8).toUpperCase())}</b></div>
    <div class="row"><span>Date</span><b>${esc(p.date)}</b></div>
    <div class="row"><span>Loan Code</span><b>${esc(code)}</b></div>
    <div class="row"><span>Customer</span><b>${esc(customerLine(loan))}</b></div>
    ${loan.address ? `<div class="row"><span>Address</span><span>${esc(loan.address)}</span></div>` : ""}
    <h2>Payment</h2>
    <div class="row"><span>Interest (Income)</span><span>${rm(p.incomeAmount)}</span></div>
    <div class="row"><span>Principal</span><span>${rm(p.principalAmount)}</span></div>
    <div class="row tot"><span>Total Received</span><span>${rm(p.totalPayment)}</span></div>
    <div class="row" style="margin-top:14px"><span>Outstanding Balance</span><b>${rm(loan.outstanding)}</b></div>
    <div class="sign"><div>Received By</div><div>Customer</div></div>`;
  openPrint(shell("Receipt " + code, inner));
}

export function printStatement(loan, payments) {
  const code = loan.customerCode || loan.code;
  const rows = (payments || []).map((p) => `<tr><td>${esc(p.date)}</td><td class="r">${rm(p.incomeAmount)}</td><td class="r">${rm(p.principalAmount)}</td><td class="r">${rm(p.totalPayment)}</td></tr>`).join("");
  const inner = `
    <div class="brand">DIAMOND SHARP</div>
    <div class="muted">Branch ${esc(loan.branch || "")} · Loan Statement</div>
    <h2>Loan Details</h2>
    <div class="row"><span>Loan Code</span><b>${esc(code)}${loan.customerCode ? ` (HQ ${esc(loan.code)})` : ""}</b></div>
    <div class="row"><span>Customer</span><b>${esc(customerLine(loan))}</b></div>
    ${loan.address ? `<div class="row"><span>Address</span><span>${esc(loan.address)}</span></div>` : ""}
    <div class="row"><span>Date Applied</span><span>${esc(loan.dateApplied)}</span></div>
    <div class="row"><span>Loan Apply</span><span>${rm(loan.loanApply)}</span></div>
    <div class="row"><span>In Hand</span><span>${rm(loan.inHand)}</span></div>
    <div class="row"><span>Repayment Formula</span><span>${esc(loan.repaymentFormula || "—")}</span></div>
    <div class="row"><span>Status</span><span>${esc(loan.status)}</span></div>
    <h2>Payment History (${(payments || []).length})</h2>
    <table><thead><tr><th>Date</th><th class="r">Interest</th><th class="r">Principal</th><th class="r">Total</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="4">No payments recorded.</td></tr>'}</tbody></table>
    <div class="row tot"><span>Total Paid</span><span>${rm(loan.totalPaid)}</span></div>
    <div class="row"><span>Outstanding Balance</span><b>${rm(loan.outstanding)}</b></div>
    <div class="row"><span>Actual Profit / Loss</span><span>${rm(loan.actualProfitLoss)}</span></div>`;
  openPrint(shell("Statement " + code, inner));
}
